import React, { useEffect, useState } from "react";
import PlaceholderPage from "./PlaceholderPage";
import { WireAPI } from "../lib/api";
import { Radio, ExternalLink } from "lucide-react";

/**
 * Syndicate hub. Renders our aggregated wire feed (already implemented) in a
 * larger, browseable layout — with room to add per-source syndication pages
 * (Precious Wire, Macro Wire) later.
 */
export const WirePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    WireAPI.list(50)
      .then((data) => {
        if (!cancelled)
          setItems(Array.isArray(data) ? data : data?.items || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <PlaceholderPage
      eyebrow="Syndicate"
      title="The Wire"
      body={
        <>
          <p>
            Live headlines syndicated into Vexent Wire from independent
            financial and geopolitical sources. Fully curated, deduplicated,
            and refreshed every ten minutes.
          </p>
          <p className="text-[13px] text-neutral-600">
            Dedicated sub-feeds (Precious Wire, Macro Wire) come next.
          </p>
        </>
      }
      testid="wire-page"
      extra={
        <div className="mt-8 border border-neutral-300 bg-white">
          <div className="bg-[#0B1F33] text-[#e2b877] px-4 py-2.5 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 animate-pulse" />
            <span className="font-condensed uppercase tracking-[0.18em] text-[12px] font-bold">
              Live Feed
            </span>
          </div>
          <ul className="divide-y divide-neutral-200">
            {loading && (
              <li className="p-4 text-[13px] text-neutral-500">Loading…</li>
            )}
            {!loading && items.length === 0 && (
              <li className="p-4 text-[13px] text-neutral-500">
                No wire items available.
              </li>
            )}
            {items.map((it, i) => (
              <li key={i} className="p-4 hover:bg-neutral-50 transition-colors">
                <a
                  href={it.link || it.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group block"
                  data-testid={`wire-item-${i}`}
                >
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#0891B2] font-condensed font-bold mb-1">
                    {(it.source || "wire").slice(0, 32)}
                    {it.publishedAt && (
                      <span className="ml-2 text-neutral-500 font-mono-tick font-normal normal-case tracking-normal">
                        {relTime(it.publishedAt)}
                      </span>
                    )}
                  </div>
                  <div className="font-condensed uppercase text-[15px] leading-tight font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors">
                    {it.title}
                    <ExternalLink className="inline w-3 h-3 ml-1 opacity-50" />
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      }
    />
  );
};

function relTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const s = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch { return ""; }
}

export default WirePage;
